import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Container, Typography } from '@mui/material'
import { useForm } from '@tanstack/react-form'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  activeLinkedServiceChanged,
  fetchGroups,
  fetchLightweightUsersV4,
  fetchLinkedServices,
  selectActiveLinkedService,
  selectGroupsStatus,
  selectGroupsWithDefaults,
  selectLightweightUsersV4ByService,
  selectLightweightUsersV4StatusByService,
  selectLinkedServicesStatus,
} from 'src/v3/manage/manage.slice'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from '../hooks/use-notifications'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import ConfirmationModal from '../components/modals/confirmation-modal'
import { fetchComplianceRequirements } from '../compliance.slice'
import { createComplianceRequirement } from '../utils/api'
import { COMPLIANCE_URLS } from '../constants'
import {
  ComplianceFormData,
  ComplianceWizardStep,
  credentialItemToPayload,
  recertItemToPayload,
} from './types'
import ComplianceWizardNavigation from './components/form-wizard-navigation'
import ComplianceDetailsStep from './components/form-details-step'
import ComplianceRequirementsStep from './components/form-requirements-step'
import ComplianceUserSelectionStep from './components/form-user-selection-step'
import ComplianceReviewStep from './components/form-review-step'

export default function ComplianceCreate() {
  let navigate = useNavigate()
  let location = useLocation()
  let dispatch = useAppDispatch()

  let linkedServiceID = useAppSelector(selectActiveLinkedService)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let linkedServiceName = useLinkedServiceName(linkedServiceID)

  let lightweightUsers = useAppSelector(
    selectLightweightUsersV4ByService(linkedServiceID || ''),
  )
  let lightweightUsersStatus = useAppSelector(
    selectLightweightUsersV4StatusByService(linkedServiceID || ''),
  )
  let groups = useAppSelector(selectGroupsWithDefaults(linkedServiceID || ''))
  let groupsStatus = useAppSelector(selectGroupsStatus)

  let [step, setStep] = useState<ComplianceWizardStep>('details')
  let [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  let [isSubmitting, setIsSubmitting] = useState(false)
  let { showError, NotificationComponent } = useNotifications()

  let form = useForm({
    defaultValues: {
      name: '',
      linkedServiceID: linkedServiceID || '',
      // issueDate: '',
      // expirationDate: '',
      alertSetting: 'Disabled',
      alertType: 'Email',
      userIDs: [],
      licenseItems: [],
      credentialItems: [],
      nremtItems: [],
      recertItems: [],
    } as ComplianceFormData,
    validators: {
      onChange: ({ value }) => {
        let fieldErrors: Array<[string, string]> = [
          ...(!value.name
            ? [['name', 'Name is required'] as [string, string]]
            : []),
          // ...(!value.issueDate
          //   ? [['issueDate', 'Issue date is required'] as [string, string]]
          //   : []),
          // ...(!value.expirationDate
          //   ? [
          //       ['expirationDate', 'Expiration date is required'] as [
          //         string,
          //         string,
          //       ],
          //     ]
          //   : []),
          ...(value.userIDs.length === 0
            ? [
                ['userIDs', 'At least one user must be selected'] as [
                  string,
                  string,
                ],
              ]
            : []),
        ]
        return fieldErrors.length > 0
          ? { fields: Object.fromEntries(fieldErrors) }
          : undefined
      },
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true)

      await createComplianceRequirement({
        ...value,
        credentialItems: value.credentialItems.map(credentialItemToPayload),
        nremtItems: value.nremtItems,
        recertItems: value.recertItems.map(recertItemToPayload),
      })
        .then(({ id }) => {
          if (linkedServiceID && linkedServiceID !== 'unselected') {
            dispatch(fetchComplianceRequirements(linkedServiceID))
          }
          navigate(COMPLIANCE_URLS.DETAIL(id))
        })
        .catch((err: unknown) => {
          let errorMessage =
            err instanceof Error ? err.message : 'Unknown error'
          // eslint-disable-next-line no-console
          console.error('Error creating compliance requirement:', errorMessage)
          showError('Failed to create compliance requirement')
        })
        .finally(() => setIsSubmitting(false))
    },
  })

  let handleCancel = (formValues?: ComplianceFormData) => {
    if (
      formValues &&
      (formValues.name ||
        formValues.userIDs.length > 0 ||
        formValues.licenseItems.length > 0 ||
        formValues.credentialItems.length > 0 ||
        formValues.nremtItems.length > 0 ||
        formValues.recertItems.length > 0)
    ) {
      setCancelDialogOpen(true)
    } else {
      navigate(COMPLIANCE_URLS.INDEX)
    }
  }

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  useEffect(() => {
    let state = location.state as { linkedServiceID?: string } | null
    if (state?.linkedServiceID && state.linkedServiceID !== linkedServiceID) {
      dispatch(activeLinkedServiceChanged(state.linkedServiceID))
      navigate(location.pathname, {
        replace: true,
        state: { ...state, linkedServiceID: undefined },
      })
    }
  }, [location.state, location.pathname, linkedServiceID, navigate, dispatch])

  useEffect(() => {
    if (
      linkedServicesStatus === 'fulfilled' &&
      (!linkedServiceID || linkedServiceID === 'unselected')
    ) {
      navigate(COMPLIANCE_URLS.INDEX)
    }
  }, [linkedServiceID, linkedServicesStatus, navigate])

  useEffect(() => {
    if (linkedServiceID && linkedServiceID !== 'unselected') {
      form.setFieldValue('linkedServiceID', linkedServiceID)
      if (
        lightweightUsersStatus !== 'fulfilled' &&
        lightweightUsersStatus !== 'pending'
      ) {
        dispatch(fetchLightweightUsersV4(linkedServiceID))
      }
    }
  }, [linkedServiceID])

  useEffect(() => {
    if (groupsStatus !== 'fulfilled' && groupsStatus !== 'pending') {
      dispatch(fetchGroups())
    }
  }, [groupsStatus])

  return (
    <Container maxWidth="xl" className="py-6">
      <PageTitleWithLinkedService
        pageTitle="Create Compliance Requirement"
        moduleName="Compliance"
        linkedServiceName={linkedServiceName || ''}
        handleBackClick={() => handleCancel(form.state.values)}
      />
      <form.Subscribe
        selector={(state) => ({
          values: state.values,
          fieldMeta: state.fieldMeta,
        })}
      >
        {({ values, fieldMeta }) => {
          let canProceedToUsers = Boolean(
            values.name &&
            fieldMeta.name?.isValid &&
            !fieldMeta.name?.errors?.length,
            // values.issueDate &&
            // fieldMeta.issueDate?.isValid &&
            // !fieldMeta.issueDate?.errors?.length &&
            // values.expirationDate &&
            // fieldMeta.expirationDate?.isValid &&
            // !fieldMeta.expirationDate?.errors?.length,
          )

          let canProceedToReview = Boolean(
            canProceedToUsers && values.userIDs.length > 0,
          )

          return (
            <>
              <ComplianceWizardNavigation
                currentStep={step}
                onStepChange={setStep}
                canProceedToUsers={canProceedToUsers}
                canProceedToReview={canProceedToReview}
              />
              {step === 'details' && (
                <ComplianceDetailsStep
                  form={form}
                  onCancel={() => handleCancel(values)}
                  onContinue={() => setStep('requirements')}
                  canContinue={canProceedToUsers}
                />
              )}
              {step === 'requirements' && (
                <ComplianceRequirementsStep
                  licenseItems={values.licenseItems}
                  credentialItems={values.credentialItems}
                  nremtItems={values.nremtItems}
                  recertItems={values.recertItems}
                  onLicenseItemsChange={(items) =>
                    form.setFieldValue('licenseItems', items)
                  }
                  onCredentialItemsChange={(items) =>
                    form.setFieldValue('credentialItems', items)
                  }
                  onNREMTItemsChange={(items) =>
                    form.setFieldValue('nremtItems', items)
                  }
                  onRecertItemsChange={(items) =>
                    form.setFieldValue('recertItems', items)
                  }
                  onCancel={() => handleCancel(values)}
                  onBack={() => setStep('details')}
                  onContinue={() => setStep('users')}
                  canContinue={canProceedToUsers}
                />
              )}
              {step === 'users' && (
                <ComplianceUserSelectionStep
                  userIDs={values.userIDs}
                  users={lightweightUsers}
                  groups={groups as any}
                  onUserIDsChange={(ids) => form.setFieldValue('userIDs', ids)}
                  onCancel={() => handleCancel(values)}
                  onBack={() => setStep('requirements')}
                  onContinue={() => setStep('review')}
                  canContinue={canProceedToReview}
                  isLoadingUsers={lightweightUsersStatus === 'pending'}
                />
              )}
              {step === 'review' && (
                <ComplianceReviewStep
                  formData={values}
                  users={lightweightUsers}
                  onCancel={() => handleCancel(values)}
                  onBack={() => setStep('users')}
                  onSubmit={() => form.handleSubmit()}
                  isSubmitting={isSubmitting}
                />
              )}
            </>
          )
        }}
      </form.Subscribe>
      <ConfirmationModal
        isOpen={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={() => navigate(COMPLIANCE_URLS.INDEX)}
        title="Cancel Requirement Creation?"
        confirmText="Yes, Cancel"
        cancelText="Continue Editing"
        confirmColor="error"
      >
        <Typography>
          Are you sure you want to cancel? Any changes will be lost.
        </Typography>
      </ConfirmationModal>
      {NotificationComponent}
    </Container>
  )
}
